<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: User Name Input -->
              <base-input
                type="text"
                col="4"
                :placeholder="$t('PLACEHOLDERS.user_name')"
                v-model="filterOptions.user_name"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: User Mobile Input -->
              <base-input
                type="text"
                col="4"
                :placeholder="$t('PLACEHOLDERS.mobile')"
                v-model="filterOptions.mobile"
              />
              <!-- End:: User Mobile Input -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="2"
                type="date"
                :placeholder="$t('PLACEHOLDERS.startDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="2"
                type="date"
                :placeholder="$t('PLACEHOLDERS.endDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.search") }}</span>
                </template>
                <span
                  class="submit_btn"
                  @click="submitFilterForm"
                  :disabled="isWaitingRequest"
                >
                  <i class="fal fa-search"></i>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom">
                <template slot="title">
                  <span>{{ $t("BUTTONS.rseet_search") }}</span>
                </template>
                <span
                  class="reset_btn"
                  :disabled="isWaitingRequest"
                  @click="resetFilter"
                >
                  <i class="fal fa-redo"></i>
                </span>
              </a-tooltip>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.financial_contracts") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <div class="excel" @click="downloadFile('excel')">
            {{ $t("BUTTONS.downloadExcel") }}
          </div>

          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadFile('pdf')"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.username`]="{ item }">
          <h6 class="text-danger" v-if="!item.username">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.username }}</h6>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: Mobile -->
        <template v-slot:[`item.mobile`]="{ item }">
          <h6 class="text-danger" v-if="!item.mobile">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.mobile }}</h6>
        </template>
        <!-- End:: Mobile -->

        <!-- Start:: Contract Type -->
        <template v-slot:[`item.contract_type`]="{ item }">
          <h6 class="text-danger" v-if="!item.contract_type">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>
            {{
              item.contract_type === "residential"
                ? $t("TABLES.Contracts.residential")
                : $t("TABLES.Contracts.commercial")
            }}
          </h6>
        </template>
        <!-- End:: Contract Type -->

        <template v-slot:[`item.contract_start_date`]="{ item }">
          <h6>{{ formatDisplayDate(item.contract_start_date) }}</h6>
        </template>

        <template v-slot:[`item.contract_end_date`]="{ item }">
          <h6>{{ formatDisplayDate(item.contract_end_date) }}</h6>
        </template>

        <!-- Start:: Total -->
        <template v-slot:[`item.total`]="{ item }">
          <h6 class="text-danger" v-if="!item.total">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.total }}</h6>
        </template>
        <!-- End:: Total -->

        <!-- Start:: Total App Profit -->
        <template v-slot:[`item.total_app_profit`]="{ item }">
          <h6 class="text-danger" v-if="!item.total_app_profit">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.total_app_profit }}</h6>
        </template>
        <!-- End:: Total App Profit -->

        <!-- Start:: Total VAT -->
        <template v-slot:[`item.total_vat`]="{ item }">
          <h6 class="text-danger" v-if="!item.total_vat">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.total_vat }}</h6>
        </template>
        <!-- End:: Total VAT -->
      </v-data-table>

      <!-- Start:: Totals Statistics Table -->
      <div class="table_content">
        <div class="table_title_wrapper">
          <div class="title_text_wrapper">
            <!-- <h5>{{ $t("PLACEHOLDERS.Overall_statistics") }}</h5> -->
          </div>
        </div>
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_fees") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalTaxValue") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_platform_profit") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template>
                <tr class="text-center all-stat">
                  <td>{{ total_fees }}</td>
                  <td>{{ total_tax }}</td>
                  <td>{{ total_app_profit }}</td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!-- End:: Totals Statistics Table -->
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllFinancialContracts",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      pdfDownloadBtnIsLoading: false,
      excelDownloadBtnIsLoading: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        mobile: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.user"),
          value: "username",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.mobile"),
          value: "mobile",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.contract_type"),
          value: "contract_type",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.contract_start_date"),
          value: "contract_start_date",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.contract_end_date"),
          value: "contract_end_date",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.total_contract_fees"),
          value: "total",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.total_app_profit"),
          value: "total_app_profit",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.total_tax_value"),
          value: "total_vat",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data

      // Start:: Totals Data
      total_fees: null,
      total_app_profit: null,
      total_tax: null,
      // End:: Totals Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    formatDisplayDate(value) {
      if (!value) return this.$t("TABLES.noData");
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return value;
      return `${match[3]}/${match[2]}/${match[1]}`;
    },

    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-contracts/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.mobile = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-contracts/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Data
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "reports/rent-contracts",
          params: {
            page: this.paginations.current_page,
            limit: 10,
            username: this.filterOptions.user_name,
            mobile: this.filterOptions.mobile,
            from: this.filterOptions.from_date,
            to: this.filterOptions.to_date,
          },
        });

        this.loading = false;

        // Extract data from nested structure
        const reportData = res.data.data.data;
        this.tableRows = reportData.report.data;

        // Set totals from per_page totals
        this.total_fees = reportData.totals.all_pages.total;
        this.total_app_profit = reportData.totals.all_pages.total_app_profit;
        this.total_tax = reportData.totals.all_pages.total_vat;

        // Set pagination
        this.paginations.last_page = reportData.report.last_page;
        this.paginations.items_per_page = reportData.report.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Handling Download Files
    async downloadFile(type) {
      const url =
        type === "pdf"
          ? "reports/rent-contracts/generate-pdf"
          : "reports/rent-contracts/generate-excel";

      try {
        const response = await this.$axios.get(url, {
          params: {
            username: this.filterOptions.user_name,
            mobile: this.filterOptions.mobile,
            from: this.filterOptions.from_date,
            to: this.filterOptions.to_date,
          },
        });

        if (response.data && response.data.data && response.data.data.data) {
          window.open(response.data.data.data, "_blank");
          this.$message.success(
            type === "pdf"
              ? this.$t("MESSAGES.pdfDownloadedSuccessfully")
              : this.$t("MESSAGES.excelDownloadedSuccessfully")
          );
        } else {
          this.$message.error(this.$t("MESSAGES.downloadLinkNotFound"));
        }
      } catch (error) {
        console.log(error);
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },
    // End:: Handling Download Files
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.all-stat {
  background: var(--main_theme_clr);
}

.all-stat td {
  color: white;
  font-size: 21px !important;
  font-weight: 800;
}

.show_all_content_wrapper
  .v-data-table
  .v-data-table__wrapper
  table
  tbody
  tr:hover {
  background: var(--main_theme_clr) !important;
  text-align: center;
}

table,
tbody,
tr,
td {
  text-align: center !important;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}

.pdf_btn {
  margin-left: 10px;
}

span.submit_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #f6a513;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

span.reset_btn {
  width: 45px;
  height: 45px;
  font-size: 16px;
  border-radius: 10px;
  color: var(--white_clr);
  transition: all 0.3s linear;
  background-color: #dc3545;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
</style>
